import styled from '@emotion/styled';
import css from '@styled-system/css';
import axios from 'axios';
import CompareIcon from 'imagesUwKluis/svg/import.svg';
import OpenSvg from 'imagesUwKluis/svg/open.svg';
import {object, string} from 'prop-types';
import {useNavigate} from 'react-router-dom';
import Box from '../../../shared/layouts/Box';
import Flex from '../../../shared/layouts/Flex';
import Stack from '../../../shared/layouts/Stack';
import WithIcon from '../../../shared/layouts/WithIcon';
import {useTranslation} from 'react-i18next';
import useIconLibrary from "../../../../hooks/useIconLibrary";

const NotificationContainer = styled(Flex)(({ theme }) => ({
  maxWidth: 1024,
  minHeight: 80,
  alignItems: 'center',
  borderRadius: theme.borderRadius,
  boxShadow: theme.boxShadows.normal,
  cursor: 'pointer',
  justifyContent: 'space-between',
}));

export const getNotificationVariant = (iconLibrary, type) => {
  const notificationTypes = {
    organization_message_created: [
      iconLibrary.MessageIcon,
      ({organization_message_uuid: id}) => [`/messages`, {state: {id}}],
    ],
    organization_file_deleted: [iconLibrary.DocumentsIcon, () => [`/documents/received`]],
    organization_file_created: [iconLibrary.DocumentsIcon, () => ['/documents/received']],
    connection_request_created: [iconLibrary.DashboardIcon, () => [`/dashboard`]],
    dossier_updated: [CompareIcon, () => [`/compare/`]],
    add_user: [iconLibrary.SettingsIcon, () => ['/user/settings']],
    document_request_created: [
      iconLibrary.DocumentsIcon,
      ({document_request_id: id}) => [`/requests/document-requests/${id}`],
    ],
    document_request_document_rejected: [
      iconLibrary.DocumentsIcon,
      ({document_request_id: id}) => [`/requests/document-requests/${id}`],
    ],
    incomplete_house: [iconLibrary.HouseIcon, () => ['/house']],
    welcome: [iconLibrary.ProfileIcon, () => ['/profile']],
    setup_2fa: [iconLibrary.ShieldIcon, () => ['/user/settings']],
    setup_generic_app: [iconLibrary.LinkIcon, () => ['/user/settings']],
    open_sign_request: [
      iconLibrary.EditIcon,
      ({request_id: id}) => id !== null ? [`/requests/sign-requests/${id}`] : ['/requests'],
    ],
    open_source_data_request: [
      iconLibrary.DocumentsIcon,
      ({request_id: id}) => id !== null ? [`/requests/source-data/${id}`] : ['/requests'],
    ],
    open_questionnaire: [
      iconLibrary.QuestionnaireIcon,
      ({request_id: id}) => id !== null ? [`/requests/questionnaires/${id}`] : ['/requests'],
    ],
    open_finly_form: [
      iconLibrary.QuestionnaireIcon,
      ({request_id: id}) => id !== null ? [`/requests/finly-forms/${id}`] : ['/requests'],
    ],
    max_mortgage: [
      iconLibrary.MortgageIcon,
      () => ['/tools/max-mortgage']
    ]
  };

  return notificationTypes[type] || null;
};

const Notification = ({ id, type, title, message, meta, mark_as_read }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const iconLibrary = useIconLibrary();
  
  const notificationType = getNotificationVariant(iconLibrary, type);
  const TypeIcon = notificationType ? notificationType[0] : null;
  return notificationType ? (
    <NotificationContainer
      onClick={() => {
        if (type === 'connection_request_created') {
          window.location.reload();
        } else {
          navigate(...notificationType[1](meta));
        }
        if (mark_as_read) {
          axios
            .post(`/user/notifications/mark/${id}/read`)
            .catch(console.error);
        }
      }}
      bg="white"
      px="l"
      py="m"
    >
      <WithIcon space="m" color="primary">
        <TypeIcon width="2rem" height="2rem" />
        <Stack space="xs">
          <Box fontWeight="bold" lineHeight={'1.25rem'}>{t(title)}</Box>
          <Box>{t(message)}</Box>
        </Stack>
      </WithIcon>
      <OpenSvg
        css={css({ fill: 'primary', flexShrink: '0' })}
        width="1.5rem"
        height="1.5rem"
      />
    </NotificationContainer>
  ) : null;
};

Notification.propTypes = {
  id: string.isRequired,
  type: string.isRequired,
  title: string.isRequired,
  message: string,
  meta: object,
};

Notification.defaultProps = {
  message: '',
};

export default Notification;
